import { AxiosError, AxiosResponse } from "axios";
import * as DetectBrowser from "detect-browser";
import * as punycode from "punycode";
import * as React from "react";
import * as ReactGA from "react-ga";
import { FaAngleDoubleLeft, FaCaretDown, FaCaretUp, FaCheck } from "react-icons/fa";
import { Language, LocalizeContextProps, Translate, withLocalize } from "react-localize-redux";
import { RouteComponentProps, withRouter } from "react-router";
import { Link } from "react-router-dom";
import { Alert, Button, Collapse, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Observable, Observer, Subject, Subscription } from "rxjs";
import scrollTo from "../helpers/scroll";
import IOffer from "../models/Offer";
import ApiService from "../services/ApiService";
import Jumbotron from "./Jumbotron";
import Navbar from "./Navbar";
import Number from "./Number";
import "./ContactRequest.scss";
import StepperMobile from "./StepperMobile";

interface IProps extends LocalizeContextProps, RouteComponentProps {
  deleteOffer: any;
  editOffer: any;
  language: Language;
  languageChanged: any;
  offer: IOffer;
  offers: IOffer[];
  pushOffer: any; // for parent App
  updateEmailSentStatus: any; // for parent App
  updateOffer: any;
}
interface IState {
  companyToDelete: string;
  finishedEnabled: boolean;
  isValidForm: boolean;
  loading: boolean;
  offersOpen: any;
  showCatalogModal: boolean;
  showDeleteModal: boolean;
  showSuccessDelete: boolean;
}
class ContactRequest extends React.Component<IProps, IState> {
  private api: ApiService = new ApiService();
  private alertClick$: Subject<any> = new Subject();
  private alertSubscription: Subscription;
  private timeout: NodeJS.Timeout;

  private offerContactInputs: {
    businessID: React.RefObject<HTMLInputElement>;
    companyName: React.RefObject<HTMLInputElement>;
    emailAddress: React.RefObject<HTMLInputElement>;
    firstName: React.RefObject<HTMLInputElement>;
    lastName: React.RefObject<HTMLInputElement>;
    phoneNumber: React.RefObject<HTMLInputElement>;
  } = {
    businessID: React.createRef(),
    companyName: React.createRef(),
    emailAddress: React.createRef(),
    firstName: React.createRef(),
    lastName: React.createRef(),
    phoneNumber: React.createRef(),
  };
  private offerCreatorInputs: {
    businessID: React.RefObject<HTMLInputElement>;
    companyName: React.RefObject<HTMLInputElement>;
    emailAddress: React.RefObject<HTMLInputElement>;
    firstName: React.RefObject<HTMLInputElement>;
    lastName: React.RefObject<HTMLInputElement>;
    phoneNumber: React.RefObject<HTMLInputElement>;
  } = {
    businessID: React.createRef(),
    companyName: React.createRef(),
    emailAddress: React.createRef(),
    firstName: React.createRef(),
    lastName: React.createRef(),
    phoneNumber: React.createRef(),
  };
  private agreeForm: React.RefObject<HTMLFormElement> = React.createRef();
  private description: React.RefObject<HTMLTextAreaElement> = React.createRef();
  private offerCreatorForm: React.RefObject<HTMLFormElement> = React.createRef();
  private offerContactForm: React.RefObject<HTMLFormElement> = React.createRef();

  public constructor(props: IProps) {
    super(props);

    const finishedEnabled = this.props.offers.every((offer: IOffer) => !!offer.pushedToBackend);

    this.state = {
      companyToDelete: "",
      finishedEnabled,
      isValidForm: false,
      loading: false,
      offersOpen: {},
      showCatalogModal: false,
      showDeleteModal: false,
      showSuccessDelete: false,
    };

    this.createOfferClicked = this.createOfferClicked.bind(this);
    this.handleDelete = this.handleDelete.bind(this);
    this.handleEdit = this.handleEdit.bind(this);
    this.handleNo = this.handleNo.bind(this);
    this.handleSaveAndNew = this.handleSaveAndNew.bind(this);
    this.handleYes = this.handleYes.bind(this);
    this.toggleOfferCollapse = this.toggleOfferCollapse.bind(this);
  }

  public render() {
    return (
      <div className="contact-request">
        <Modal isOpen={this.state.showCatalogModal} toggle={this.handleNo} centered={true}>
          <ModalHeader toggle={this.handleNo}>
            <Translate id="CatalogMustBeUpdated" />
          </ModalHeader>
          <ModalBody>
            <Translate id="CatalogMustBeUpdatedText" />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" aria-label="Close" onClick={this.handleNo}>
              <Translate id="No" />
            </Button>
            <Button color="success" onClick={this.handleYes}>
              <Translate id="Yes" />
            </Button>
          </ModalFooter>
        </Modal>
        <Modal isOpen={this.state.showDeleteModal} toggle={this.handleNo} centered={true}>
          <ModalHeader toggle={this.handleNo}>
            <Translate id="DeleteOffer" />
          </ModalHeader>
          <ModalBody>
            <Translate id="DeleteOfferText" data={{ company: this.state.companyToDelete }} />
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" aria-label="Close" onClick={this.handleNo}>
              <Translate id="Cancel" />
            </Button>
            <Button color="danger" onClick={this.handleYes}>
              <Translate id="Delete" />
            </Button>
          </ModalFooter>
        </Modal>
        <Alert color="success" isOpen={this.state.showSuccessDelete} toggle={this.handleNo}>
          <span className="check mr-3">
            <FaCheck />
          </span>
          <Translate id="DeleteOfferSuccess" />
        </Alert>
        <Navbar current={2} summaryEnabled={true} finishedEnabled={this.state.finishedEnabled} language={this.props.language} languageChanged={this.props.languageChanged} />
        <Jumbotron />
        <div className="main-container container">
          <StepperMobile current={2} finishedEnabled={this.state.finishedEnabled} />
          <div className="my-5">
            <div className="my-5">
              <h3>
                <Translate id="ContactRequest" />
              </h3>
              <form ref={this.offerContactForm} className="my-3 row needs-validation" noValidate={true}>
                <div className="col-12 my-3">
                  <label>
                    <Translate id="Company" /> *
                  </label>
                  <input type="text" name="company" className="form-control" defaultValue={this.props.offer.offerContact.companyName} ref={this.offerContactInputs.companyName} required={true} />
                  <div className="invalid-feedback">
                    <Translate id="CompanyNameIsRequired" />
                  </div>
                </div>
                <div className="col-12 col-md-6 my-3">
                  <label>
                    <Translate id="FirstName" />
                  </label>
                  <input type="text" name="firstname" className="form-control" defaultValue={this.props.offer.offerContact.firstName} ref={this.offerContactInputs.firstName} required={false} />
                  <div className="invalid-feedback">
                    <Translate id="FirstNameIsRequired" />
                  </div>
                </div>
                <div className="col-12 col-md-6 my-3">
                  <label>
                    <Translate id="LastName" />
                  </label>
                  <input type="text" name="lastname" className="form-control" defaultValue={this.props.offer.offerContact.lastName} ref={this.offerContactInputs.lastName} required={false} />
                  <div className="invalid-feedback">
                    <Translate id="LastNameIsRequired" />
                  </div>
                </div>
                <div className="col-12 my-3">
                  <label>
                    <Translate id="Email" /> *
                  </label>
                  <input type="email" name="email" className="form-control" ref={this.offerContactInputs.emailAddress} required={true} />
                  <div className="invalid-feedback">
                    <Translate id="EmailIsRequired" />
                  </div>
                </div>
                <div className="col-12 col-md-6 my-3">
                  <label>
                    <Translate id="Phone" /> *
                  </label>
                  <input type="number" name="phone" className="form-control" defaultValue={this.props.offer.offerContact.phoneNumber} ref={this.offerContactInputs.phoneNumber} required={true} />
                  <div className="invalid-feedback">
                    <Translate id="PhoneIsRequired" />
                  </div>
                </div>
                <div className="col-12 my-3">
                  <label>
                    <Translate id="MoreInfo" />
                  </label>
                  <textarea className="form-control" rows={3} defaultValue={this.props.offer.description} ref={this.description} required={false} />
                </div>
              </form>
            </div>
            <div className="my-4">
              {this.props.offers.filter((o: IOffer) => o.publicIdentifier !== this.props.offer.publicIdentifier).length ? (
                <div>
                  <div className="font-weight-bold">
                    <Translate id="PreviousOffers" />
                  </div>
                  <hr />
                  {this.props.offers
                    .filter((o: IOffer) => o.publicIdentifier !== this.props.offer.publicIdentifier)
                    .map((o: IOffer, idx) => {
                      return (
                        <div key={idx} className="previous-offer">
                          <a className="row" data-idx={idx} onClick={this.toggleOfferCollapse}>
                            <div className="col-auto offer-number">{idx + 1}</div>
                            <div className="col">
                              {o.offerContact.companyName || (
                                <span>
                                  <Translate id="Company" /> {idx + 1}
                                </span>
                              )}
                              {o.offerContact.businessID ? <span>, {o.offerContact.businessID}</span> : null}
                            </div>
                            <span className="col text-right netvisor-link">
                              {this.state.offersOpen[idx] ? (
                                <span>
                                  <Translate id="Hide" />
                                  <FaCaretUp />
                                </span>
                              ) : (
                                <span>
                                  <Translate id="ExpandOffer" />
                                  <FaCaretDown />
                                </span>
                              )}
                            </span>
                          </a>
                          <Collapse className="my-2" isOpen={this.state.offersOpen[idx]}>
                            <div className="offer-details">
                              <div className="row">
                                <div className="col-3 d-none d-sm-block">
                                  <Translate id="Package" />
                                </div>
                                <div className="col-6">
                                  <Translate id={o.selection.product.sku + ".name"} />
                                </div>
                                <div className="col text-right">
                                  <Number value={o.selection.product.price || 0} /> <Translate id="EuroPerMonth" />
                                </div>
                              </div>
                              {o.selection.extraServices
                                .filter((extra: any) => extra.selected !== false && (extra.sku === "NV-tr-ml" || extra.sku === "NV-tr-ol"))
                                .map((s: any, k: number) => {
                                  return (
                                    <div key={k} className="row justify-content-between">
                                      <div className="offset-0 offset-sm-3 col-9 col-sm-6">
                                        <Translate id={s.sku + ".name"} />{" "}
                                        <span>
                                          ({s.quantity} <Translate id="Pieces" />)
                                        </span>
                                      </div>
                                      <div className="col text-right">
                                        <Number value={s.price} /> <Translate id="EuroPerMonth" />
                                      </div>
                                    </div>
                                  );
                                })}
                              <hr />
                              {o.selection.extraServices
                                .filter((extra: any) => extra.selected !== false && extra.sku !== "NV-verk-p" && extra.sku !== "NV-tri" && extra.sku !== "NV-net" && extra.sku !== "NV-tr-ml" && extra.sku !== "NV-tr-ol")
                                .map((extra: any, j: number, arr: any[]) => {
                                  return (
                                    <div key={j}>
                                      <div className="row mt-2">
                                        <div className="col-3 d-none d-sm-block">{j === 0 ? <Translate id="Extras" /> : null}</div>
                                        <div className="col-6">
                                          <Translate id={extra.sku + ".name"} />
                                          {extra.quantity ? (
                                            <span>
                                              {" (" + extra.quantity + " "}
                                              <Translate>
                                                {({ translate }) => {
                                                  return <span>{translate(extra.sku + ".unit", undefined, { onMissingTranslation: () => "" + translate("Pieces") })}</span>;
                                                }}
                                              </Translate>
                                              {")"}
                                            </span>
                                          ) : null}
                                          {extra.sku === "NV-mkl" ? (
                                            <span>
                                              , <Translate id="BasedOnActual" />
                                            </span>
                                          ) : null}
                                        </div>
                                        <div className="col text-right">
                                          <Number value={extra.price || 0} /> <Translate id="EuroPerMonth" />
                                        </div>
                                      </div>
                                      {j === arr.length - 1 ? <hr /> : null}
                                    </div>
                                  );
                                })}
                              <div className="row font-weight-bold justify-content-between">
                                <div className="col-auto">
                                  <Translate id="PackageAndExtras" />
                                </div>
                                <div className="col-auto text-right">
                                  <Number value={o.prices ? o.prices.totalPrice || NaN : NaN} /> <Translate id="EuroPerMonth" />
                                </div>
                              </div>
                              <hr className="dark" />
                              {o.selection.extraServices
                                .filter((e) => e.selected !== false && e.sku === "NV-net")
                                .map((openingFee: any, h: number) => {
                                  return (
                                    <div key={h}>
                                      <div className="row">
                                        <div className="col-auto col-sm-3">
                                          <Translate id={openingFee.sku + ".name"} />
                                        </div>
                                        <div className="col-6 d-none d-sm-block">
                                          <Translate id="OnetimeFee" />
                                        </div>
                                        <div className="col text-right">
                                          <Number value={openingFee.price || 0} /> €
                                        </div>
                                      </div>
                                      <hr />
                                    </div>
                                  );
                                })}
                              {(o.selection.trainings || []).map((training: any, k: number, arr: any[]) => {
                                return (
                                  <div key={k}>
                                    <div className="row mt-2">
                                      <div className="col-3 d-none d-sm-block">{k === 0 ? <Translate id="Training" /> : null}</div>
                                      <div className="col-6">
                                        <Translate id={training.sku + ".name"} />
                                      </div>
                                      <div className="col text-right">
                                        <Number value={training.price || 0} /> €
                                      </div>
                                    </div>
                                    {k === arr.length - 1 ? <hr /> : null}
                                  </div>
                                );
                              })}
                              {o.prices && o.prices.usageFees ? (
                                <div>
                                  <div className="row justify-content-between">
                                    <div className="col-auto">
                                      <Translate id="UsageFees" />
                                    </div>
                                    <div className="col-auto text-right">
                                      <Number value={o.prices.usageFees || 0} /> <Translate id="EuroPerMonth" />
                                    </div>
                                  </div>
                                  <hr />
                                </div>
                              ) : null}
                              <div className="row mt-3">
                                <div className="col text-right small">
                                  <Translate id="Prices" /> <Translate id="Tax0" />
                                </div>
                              </div>
                              {o.description ? (
                                <div className="row mt-4">
                                  <div className="col-3 d-none d-sm-block">
                                    <Translate id="Additional" />
                                  </div>
                                  <div className="col description">{o.description}</div>
                                </div>
                              ) : null}
                              <div className="mt-4 text-right">
                                <a className="netvisor-link small" data-offertoken={o.token} onClick={this.handleEdit}>
                                  <Translate id="EditOffer" />
                                </a>
                                <span className="mx-2">|</span>
                                <a className="netvisor-link small" data-offertoken={o.token} data-company={o.offerContact.companyName || this.props.translate("Company") + " " + (idx + 1)} onClick={this.handleDelete}>
                                  <Translate id="DeleteOffer" />
                                </a>
                              </div>
                            </div>
                          </Collapse>
                          <hr />
                        </div>
                      );
                    })}
                </div>
              ) : null}
            </div>
            {/* FOOTER */}
            <div className="mt-5 mb-3">
              <hr />
              <form ref={this.agreeForm} className="needs-validation" noValidate={true}>
                <label className="form-check custom-check">
                  <input className="form-check-input" type="checkbox" value="" id="invalidCheck" required={true} />
                  <span className="checkmark" />

                  <label className="form-check-label" htmlFor="invalidCheck">
                    <Translate>
                      {({ translate }) => {
                        return <span className="small">{translate("AgreeToTermsText", undefined, { renderInnerHtml: true })}</span>;
                      }}
                    </Translate>{" "}
                    <span>*</span>
                  </label>

                  <div className="invalid-feedback">
                    <Translate id="CheckboxIsRequired" />
                  </div>
                </label>
              </form>
            </div>
            <div className="my-4">
              <hr className="darker" />
              <div className="my-3 row justify-content-between">
                <div className="col-12 col-md">
                  <div>
                    <Link to="/" className="netvisor-link small font-weight-bold">
                      <FaAngleDoubleLeft /> <Translate id="BackToPreviousPage" />
                    </Link>
                  </div>
                </div>
                <div className="col-auto mt-2">
                  <button className={"d-flex justify-content-center btn btn-success " + (this.state.loading ? "loading" : "")} disabled={this.state.loading} onClick={this.createOfferClicked}>
                    <span className={"text " + (this.state.loading ? "invisible" : "visible")}>
                      <Translate id="Send" />
                    </span>
                    <span className={"spinner-border text-success " + (this.state.loading ? "visible" : "invisible")} role="status">
                      <span className="sr-only">
                        <Translate id="Loading" />
                        ...
                      </span>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  public componentDidMount() {
    if (process.env.REACT_APP_PRODUCTION === "true") {
      // Analytics page view
      ReactGA.pageview("/contactRequest");
    }

    const browser = DetectBrowser.detect();
    if (this.offerContactInputs.emailAddress.current) {
      let emailValue = this.props.offer.offerContact.emailAddress || "";
      // Chrome will punycode all email fields so we need this convert
      if (browser && browser.name === "chrome") {
        emailValue = punycode.toASCII(emailValue);
      }
      this.offerContactInputs.emailAddress.current.value = emailValue;
    }
    if (this.offerCreatorInputs.emailAddress.current) {
      let emailValue = this.props.offer.offerCreator.emailAddress || "";
      // Chrome will punycode all email fields so we need this convert
      if (browser && browser.name === "chrome") {
        emailValue = punycode.toASCII(emailValue);
      }
      this.offerCreatorInputs.emailAddress.current.value = emailValue;
    }
  }

  public componentWillUnmount() {
    clearTimeout(this.timeout);
  }

  private createOfferClicked() {
    this.setState({ loading: true });

    this.handleMakeOffer(true, true).subscribe(
      (response: { newOffer: IOffer; previousOffer: IOffer }) => {
        if (response.newOffer && (response.newOffer.token || response.newOffer.publicIdentifier)) {
          this.props.updateOffer({
            ...response.previousOffer,
            groupId: response.newOffer.groupId,
            publicIdentifier: response.newOffer.publicIdentifier,
            pushedToBackend: true,
            token: response.newOffer.token,
          });
        } else {
          this.props.updateOffer({
            ...response.previousOffer,
            pushedToBackend: true,
          });
        }

        const token = (response.newOffer && response.newOffer.token) || (response.previousOffer && response.previousOffer.token);
        if (token) {
          this.api.sendEmail(token, this.props.activeLanguage ? this.props.activeLanguage.code : "", process.env.REACT_APP_FE_MODE === "embed").subscribe(
            (res: any) => {
              this.props.updateEmailSentStatus(true, response.previousOffer.offerCreator.emailAddress);
              this.setState({ loading: false });
              this.props.history.push("/finished");
            },
            (error: any) => {
              this.props.updateEmailSentStatus(false, response.previousOffer.offerCreator.emailAddress);
              console.error(error);
              this.setState({ loading: false });
              this.props.history.push("/finished");
            }
          );

          this.handleOfferCreatedAnalytics();
        } else {
          console.error("No token!", response);
        }
      },
      (error: string) => {
        this.setState({ loading: false });
      }
    );
  }

  private handleOfferCreatedAnalytics() {
    if (process.env.REACT_APP_PRODUCTION === "true") {
      if (process.env.REACT_APP_FE_MODE === "internal") {
        // myynti.netvisor.fi
        ReactGA.event({
          action: "offer",
          category: "myynti",
        });
      } else if (process.env.REACT_APP_FE_MODE === "embed") {
        // hinta.netvisor.fi
        ReactGA.event({
          action: "offer",
          category: "verkko",
        });
      } else {
        // laskuri.netvisor.fi
        ReactGA.event({
          action: "offer",
          category: "ao",
        });
      }
    }
  }

  private handleMakeOffer(validateTerms?: boolean, combineContacts?: boolean) {
    return Observable.create((observer: Observer<any>) => {
      // validate forms
      if (this.offerContactForm.current && this.agreeForm.current) {
        let valid = true;
        if (this.offerContactForm.current.checkValidity() === false) {
          this.offerContactForm.current.classList.add("was-validated");
          valid = false;
        }
        if (this.offerCreatorForm.current && this.offerCreatorForm.current.checkValidity() === false) {
          this.offerCreatorForm.current.classList.add("was-validated");
          valid = false;
        }
        if (validateTerms && this.agreeForm.current.checkValidity() === false) {
          this.agreeForm.current.classList.add("was-validated");
          valid = false;
        }
        if (!valid) {
          observer.error("Invalid Form");
          observer.complete();
          this.setState({ loading: false });

          // Scroll to the first invalid form-control
          scrollTo(document.querySelector(".was-validated .form-control:invalid"));

          return;
        }
      }

      const offer = Object.assign({}, this.props.offer);

      const offerContact = {
        businessID: this.offerContactInputs.businessID.current ? this.offerContactInputs.businessID.current.value : "",
        companyName: this.offerContactInputs.companyName.current ? this.offerContactInputs.companyName.current.value : "",
        emailAddress: punycode.toUnicode(this.offerContactInputs.emailAddress.current ? this.offerContactInputs.emailAddress.current.value : ""),
        firstName: this.offerContactInputs.firstName.current ? this.offerContactInputs.firstName.current.value : "",
        lastName: this.offerContactInputs.lastName.current ? this.offerContactInputs.lastName.current.value : "",
        phoneNumber: this.offerContactInputs.phoneNumber.current ? this.offerContactInputs.phoneNumber.current.value : "",
      };

      offer.offerContact = offerContact;
      offer.isContactRequest = true;
      if (this.description.current !== null) {
        offer.description = this.description.current.value;
      }
      if (process.env.REACT_APP_FE_MODE !== "embed") {
        const offerCreator = offer.offerCreator;
        if (combineContacts) {
          // Find other offers that have different creatorContact and update them if needed
          const offers: any[] = this.props.offers
            .filter((o: IOffer) => o.publicIdentifier !== offer.publicIdentifier)
            .map((o: IOffer) => {
              // Compare if creatorContact has changed
              let isSame = true;
              for (const key in offerCreator) {
                if (offerCreator[key] !== o.offerCreator[key]) {
                  isSame = false;
                  break;
                }
              }
              return isSame ? null : { ...o, offerCreator };
            })
            .filter((o) => !!o); // filter out nulls

          // Save all offers that had old contact info

          offers.forEach((o: IOffer) => {
            this.api.saveOffer(o).subscribe((data: any) => {
              // Contacts saved properly!
            });
          });
        }
      }

      // Post offer to backend
      this.api.saveOffer(offer).subscribe(
        (data: IOffer) => {
          observer.next({ newOffer: data, previousOffer: offer });
          observer.complete();
        },
        (error: AxiosError) => {
          if (error.response && error.response.status === 409) {
            // show a dialog to the user that catalog must be updated
            this.setState({ showCatalogModal: true });
            if (this.alertSubscription) {
              this.alertSubscription.unsubscribe();
            }
            this.alertSubscription = this.alertClick$.subscribe(() => {
              this.setState({ showCatalogModal: false });
              this.api.saveOffer(offer, true).subscribe(
                (data: IOffer) => {
                  observer.next({ newOffer: data, previousOffer: offer });
                  observer.complete();
                },
                (newError: AxiosError) => {
                  observer.error(newError);
                  console.error("saving offer failed with force", newError);
                }
              );
            });
          } else {
            observer.error(error);
            console.error("saving offer failed", error);
          }
        }
      );
    });
  }

  private handleYes() {
    this.alertClick$.next();
  }
  private handleNo() {
    this.setState({
      loading: false,
      showCatalogModal: false,
      showDeleteModal: false,
      showSuccessDelete: false,
    });
  }

  private toggleOfferCollapse(event: React.FormEvent<HTMLAnchorElement>) {
    if (event.currentTarget.dataset.idx !== undefined) {
      const offersOpen = Object.assign({}, this.state.offersOpen);
      offersOpen[event.currentTarget.dataset.idx] = !offersOpen[event.currentTarget.dataset.idx];
      this.setState({ offersOpen });
    }
  }

  private handleSaveAndNew(event: React.MouseEvent<HTMLElement>) {
    event.preventDefault(); // Prevent Link redirect

    this.handleMakeOffer().subscribe(
      (response: { newOffer: IOffer; previousOffer: IOffer }) => {
        if (response.newOffer && (response.newOffer.token || response.newOffer.publicIdentifier || response.newOffer.groupId)) {
          this.props.updateOffer({
            ...response.previousOffer,
            groupId: response.newOffer.groupId,
            publicIdentifier: response.newOffer.publicIdentifier,
            pushedToBackend: true,
            token: response.newOffer.token,
          });
        }

        const newGroupId = response.newOffer && response.newOffer.groupId ? response.newOffer.groupId : response.previousOffer.groupId;
        this.props.pushOffer({
          groupId: newGroupId,
          offerCreator: response.previousOffer.offerCreator,
        });
        this.props.history.push("/");
      },
      (error: string) => {
        // do nothing
      }
    );
  }

  private handleEdit(event: React.FormEvent<HTMLAnchorElement>) {
    const editableOfferToken = event.currentTarget.dataset.offertoken;

    // save current offer first
    this.handleMakeOffer().subscribe(
      (response: { newOffer: IOffer; previousOffer: IOffer }) => {
        if (response.newOffer && (response.newOffer.token || response.newOffer.publicIdentifier || response.newOffer.groupId)) {
          this.props.updateOffer({
            ...response.previousOffer,
            groupId: response.newOffer.groupId,
            publicIdentifier: response.newOffer.publicIdentifier,
            pushedToBackend: true,
            token: response.newOffer.token,
          });
        }

        this.props.editOffer(editableOfferToken);
      },
      (error: string) => {
        // do nothing
      }
    );
  }
  private handleDelete(event: React.FormEvent<HTMLAnchorElement>) {
    const offertoken = event.currentTarget.dataset.offertoken || "";
    this.setState({
      companyToDelete: event.currentTarget.dataset.company || "",
      showDeleteModal: true,
    });
    if (this.alertSubscription) {
      this.alertSubscription.unsubscribe();
    }
    this.alertSubscription = this.alertClick$.subscribe(() => {
      this.setState({ showDeleteModal: false });
      this.api.deleteOffer(offertoken).subscribe((res: AxiosResponse<any>) => {
        this.props.deleteOffer(offertoken);

        this.setState({ showSuccessDelete: true });

        clearTimeout(this.timeout);
        this.timeout = setTimeout(() => {
          this.setState({ showSuccessDelete: false });
        }, 15000);
      });
    });
  }
}

export default withLocalize(withRouter<IProps>(ContactRequest));
